/**
 * @fileoverview When the program emails go out: the day and hour of each
 * program's weekly recap and week ahead (PUT /org/email-schedule), in the
 * organization's own time zone. Part of the Notifications page.
 *
 * The organization owns the clock and each program owns the switch: a
 * program's Settings tab (ProgramEmailSettings) says whether it sends either
 * email and who is on its list; this sets the time for all of them. They go to
 * the program's people rather than its instructors: the organization's
 * administrators, the addresses added on that tab, and viewers granted them
 * (services/program_digest_service.recipients_for).
 *
 * It sat under Settings → Features until it joined the organization's other
 * emails here. It reads the organization from the portal's auth context, not
 * the notification settings, so it is there even when those cannot be loaded.
 * Each change saves at once, optimistically, and rolls back if the save fails.
 * The endpoint writes all four times, so the changes go out one at a time,
 * each built on the times the server last confirmed (useSaveQueue): one built
 * on the screen's copy could carry a refused change or undo one that landed.
 */

import { useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { OrgAuthContext } from "@/App";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Card, SectionHeader } from "@/components/studio/primitives";
import useSaveQueue from "@/components/org/useSaveQueue";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const HOURS = Array.from({ length: 24 }, (_, h) => ({
  value: h,
  label: `${((h + 11) % 12) + 1}:00 ${h < 12 ? "am" : "pm"}`,
}));

const SELECT =
  "rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-3 py-2 " +
  "font-sans text-[14px] text-[var(--text-primary)] outline-none focus:border-[var(--accent-yellow)]";

/** The two emails, each with the organization fields that hold its day and hour. */
const EMAILS = [
  {
    label: "Weekly recap",
    desc: "What was taught last week, by instructor.",
    day: "recap_weekday",
    hour: "recap_hour_local",
    testId: "recap",
  },
  {
    label: "Week ahead",
    desc: "Sessions scheduled for the coming week, by instructor.",
    day: "week_ahead_weekday",
    hour: "week_ahead_hour_local",
    testId: "week-ahead",
  },
];

/** The organization's send times, or the server's defaults where none was set. */
export function scheduleFrom(org) {
  return {
    recap_weekday: org?.recap_weekday ?? 0,
    recap_hour_local: org?.recap_hour_local ?? 8,
    week_ahead_weekday: org?.week_ahead_weekday ?? 6,
    week_ahead_hour_local: org?.week_ahead_hour_local ?? 17,
  };
}

const FIELDS = EMAILS.flatMap((e) => [e.day, e.hour]);
const LIMITS = { recap_weekday: 6, recap_hour_local: 23, week_ahead_weekday: 6, week_ahead_hour_local: 23 };

/** Whether an answer names all four times, each in range. */
const isSchedule = (s) => !!s && FIELDS.every((f) => Number.isInteger(s[f]) && s[f] >= 0 && s[f] <= LIMITS[f]);
const sameSchedule = (a, b) => FIELDS.every((f) => a[f] === b[f]);

/**
 * How long a save made here outranks an organization read with other times:
 * a read sent before the save landed can be answered after it. As on iOS.
 */
export const SAVE_OUTRANKS_READS_MS = 60 * 1000;

export default function ProgramEmailSchedule() {
  const { org, refresh } = useContext(OrgAuthContext) || {};
  const [schedule, setSchedule] = useState(() => scheduleFrom(org));
  const { run, confirm, busy } = useSaveQueue(setSchedule);
  // This page's last confirmed save and when it landed.
  const lastSaved = useRef(null);

  // The organization is the truth whenever it changes (a refresh after a
  // save), except that one with other times, arriving within a minute of a
  // save made here, was read before that save landed.
  useEffect(() => {
    const fromOrg = scheduleFrom(org);
    const saved = lastSaved.current;
    if (saved && !sameSchedule(fromOrg, saved.schedule) && Date.now() - saved.at < SAVE_OUTRANKS_READS_MS) return;
    lastSaved.current = null;
    confirm(fromOrg);
    if (!busy()) setSchedule(fromOrg);
  }, [org, confirm, busy]);

  /* One field's new time, saved on top of the times last confirmed. */
  const save = async (key, value) => {
    setSchedule((shown) => ({ ...shown, [key]: value })); // optimistic
    try {
      await run(async (current) => {
        const next = { ...current, [key]: value };
        const res = await orgApi.put("/org/email-schedule", next);
        const saved = isSchedule(res.data?.schedule) ? res.data.schedule : next;
        lastSaved.current = { schedule: saved, at: Date.now() };
        return saved;
      });
      refresh?.();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't save"));
    }
  };

  return (
    <section className="mt-8" data-testid="org-email-schedule">
      <SectionHeader className="mb-2">Program emails</SectionHeader>
      <p className="mb-4 font-sans text-[13px] leading-relaxed text-[var(--text-tertiary)]">
        When the weekly recap and week ahead go out, in {org?.timezone || "UTC"}. Each program decides on its Settings
        tab whether it sends them, and to whom; this sets the time for all of them.
      </p>
      <Card padding={0}>
        {EMAILS.map((e, i) => (
          <div
            key={e.testId}
            className={`flex flex-wrap items-center justify-between gap-x-4 gap-y-3 px-5 py-5 md:px-6 ${
              i < EMAILS.length - 1 ? "border-b border-[var(--studio-divider)]" : ""
            }`}
          >
            {/* Grows from 220px so a longer description wraps before the
                selects do; on a phone they drop beneath it. */}
            <div className="min-w-0 grow basis-[220px]">
              <div className="font-sans text-[15px] font-medium text-[var(--text-primary)]">{e.label}</div>
              <div className="mt-1 font-sans text-[13px] leading-relaxed text-[var(--text-tertiary)]">{e.desc}</div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <select
                aria-label={`${e.label} day`}
                data-testid={`${e.testId}-day`}
                value={schedule[e.day]}
                onChange={(ev) => save(e.day, Number(ev.target.value))}
                className={SELECT}
              >
                {DAYS.map((d, n) => (
                  <option key={d} value={n}>
                    {d}
                  </option>
                ))}
              </select>
              <select
                aria-label={`${e.label} time`}
                data-testid={`${e.testId}-hour`}
                value={schedule[e.hour]}
                onChange={(ev) => save(e.hour, Number(ev.target.value))}
                className={SELECT}
              >
                {HOURS.map((h) => (
                  <option key={h.value} value={h.value}>
                    {h.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ))}
      </Card>
    </section>
  );
}
